/* eslint-disable */
// components/SupabaseActions.tsx
import React, { useMemo, useState, useEffect, useContext } from "react";
import { GlobalActionsProvider, useGlobalActions } from "@plasmicapp/react-web/lib/host";
import { SupabaseClientContext } from "./SupabaseClientContext";
import { supabaseClient } from "../lib/supabase/client";
import { isPlasmicProxy } from "../lib/isPlasmicProxy";
import { getProxyFingerprint } from "../lib/proxy-client";
import { notification } from "antd";  
export type Json = string | number | boolean | null | { [key: string]: Json } | Json[];

interface MutationResult {
  success: boolean;
  status: number | null;
  data: Json | null;
  message: string;
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

const IS_CLIENT = typeof window !== "undefined";

export const SupabaseActions = ({ children }: { children: React.ReactNode }) => {
  const contextClient = useContext(SupabaseClientContext);
  const supabase = contextClient || supabaseClient;
  const globalActions = useGlobalActions();
  const [useDevProxy, setUseDevProxy] = useState(false);

  useEffect(() => {
    if (IS_CLIENT) {
      setUseDevProxy(isPlasmicProxy());
    }
  }, []);

  const actions = useMemo(() => {
const notify = (result: MutationResult) => {  
      const showNotificationAction = globalActions?.["plasmic-antd5-config-provider.showNotification"];  
      
      // Default duration is 5 seconds. If it's an error (!result.success), make it 10 seconds.
      const duration = result.success ? 5 : 10;
        
      if (showNotificationAction) {  
        // Use Plasmic's global action when available (dev)  
        const category = result.success ? "success" : "error";  
        showNotificationAction(category, result.message, "", duration, "top");  
      } else {  
        // Fallback to antd's notification API directly (production)  
        notification[result.success ? "success" : "error"]({  
          message: result.message,  
          duration: duration,  
          placement: "top",  
        });  
      }  
    };


    const applyFilters = (query: any, filters: Record<string, Json>) => {
      let q = query;
      for (const op of Object.keys(filters)) {
        const fn = q[op];
        if (typeof fn === "function") {
          const filterGroup = filters[op] as Record<string, Json>;
          for (const col of Object.keys(filterGroup)) {
            q = fn.call(q, col, filterGroup[col]);
          }
        }
      }
      return q;
    };

    const proxyFetch = async (body: Record<string, unknown>): Promise<MutationResult> => {
      console.log("[SupabaseActions] Using DEV PROXY. Sending request:", body.operation);
      try {
        // --- ADDED: Wait for fingerprint handshake ---
        const fingerprint = await getProxyFingerprint();

        const resp = await fetch("/api/plasmic-studio-proxy", {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            // --- ADDED: Include the fingerprint ---
            "x-proxy-fingerprint": fingerprint || ''
          },
          body: JSON.stringify(body),
        });

        if (resp.status === 204) { // Handle successful delete
          return { success: true, status: 204, data: null, message: `${body.operation} successful!` };
        }

        const responseData = await resp.json().catch(() => null);

        if (!resp.ok) {
          const message = responseData?.error || `Proxy error: ${resp.status}`;
          return { success: false, status: resp.status, data: null, message };
        }

        return { success: true, status: resp.status, data: responseData as Json, message: `${body.operation} successful!` };
      } catch (error: unknown) {
        console.error("[SupabaseActions] Proxy fetch failed:", error);
        const message = error instanceof Error ? error.message : "A network error occurred.";
        return { success: false, status: 500, data: null, message };
      }
    };

    const handleAction = async (
      actionName: string,
      proxyPayload: Record<string, unknown>,
      directSupabaseCall: () => Promise<MutationResult>,
      showNotification: boolean
    ) => {
      let result;
      if (useDevProxy) {
        result = await proxyFetch(proxyPayload);
      } else {
        if (!supabase) return { success: false, status: null, data: null, message: "Supabase client not available." };
        result = await directSupabaseCall();
      }
      if (showNotification) notify(result);
      return result;
    };

    return {
      create: (type: "rpc" | "table", name: string, body: Json, upsert = false, showNotification = true) => handleAction(
        "create",
        { operation: type === "rpc" ? "rpc" : (upsert ? "upsert" : "insert"), name, body },
        async () => {
          try {
            let resultPromise;
            if (type === "rpc") {
              resultPromise = (supabase as any).rpc(name, body);
            } else {
              const tableRef = (supabase as any).from(name);
              const query = upsert ? tableRef.upsert(body) : tableRef.insert(body);
              resultPromise = query.select();
            }
            const { data, error } = await resultPromise;
            if (error) return { success: false, status: parseInt(error.code) || null, data: null, message: error.message };
            return { success: true, status: 200, data: data as Json, message: "Create operation successful!" };
          } catch (e: any) { return { success: false, status: 500, data: null, message: e.message }; }
        },
        showNotification
      ),
      update: (tableName: string, filters: Record<string, Json>, body: Json, showNotification = true) => handleAction(
        "update",
        { operation: "update", table: tableName, filters, body },
        async () => {
          try {
            let query = (supabase as any).from(tableName).update(body);
            query = applyFilters(query, filters);
            const { data, error } = await query.select();
            if (error) return { success: false, status: parseInt(error.code) || null, data: null, message: error.message };
            return { success: true, status: 200, data: data as Json, message: "Update successful!" };
          } catch (e: any) { return { success: false, status: 500, data: null, message: e.message }; }
        },
        showNotification
      ),
      delete: (tableName: string, filters: Record<string, Json>, showNotification = true) => handleAction(
        "delete",
        { operation: "delete", table: tableName, filters },
        async () => {
          try {
            let query = (supabase as any).from(tableName).delete();
            query = applyFilters(query, filters);
            const { error } = await query;
            if (error) return { success: false, status: parseInt(error.code) || null, data: null, message: error.message };
            return { success: true, status: 204, data: null, message: "Delete successful!" };
          } catch (e: any) { return { success: false, status: 500, data: null, message: e.message }; }
        },
        showNotification
      ),
      select: (tableName: string, selectQuery = "*", filters?: Record<string, Json>, showNotification = true) => handleAction(
        "select",
        { operation: "select", table: tableName, select: selectQuery, filters },
        async () => {
            try {
                let query = (supabase as any).from(tableName).select(selectQuery);
                if (filters) query = applyFilters(query, filters);
                const { data, error } = await query;
                if (error) return { success: false, status: parseInt(error.code) || null, data: null, message: error.message };
                return { success: true, status: 200, data: data as Json, message: "Select successful!" };
            } catch (e: any) { return { success: false, status: 500, data: null, message: e.message }; }
        },
        showNotification
      ),
      upload: (bucketName: string, filePath: string, fileContents: string, fileType: string, upsert = false, showNotification = true) => handleAction(
        "upload",
        { operation: "upload", bucketName, filePath, fileContents, fileType, upsert },
        async () => {
          try {
            const fileBlob = base64ToBlob(fileContents, fileType);
            const { data: uploadData, error } = await (supabase as any).storage.from(bucketName).upload(filePath, fileBlob, { upsert });
            if (error) return { success: false, status: null, data: null, message: error.message };
            const { data: publicUrlData } = (supabase as any).storage.from(bucketName).getPublicUrl(uploadData.path);
            return { success: true, status: 200, data: { ...uploadData, publicUrl: publicUrlData.publicUrl }, message: "File uploaded!" };
          } catch (e: any) { return { success: false, status: 500, data: null, message: e.message }; }
        },
        showNotification
      ),
      logout: async (scope: 'local' | 'others' | 'global' = 'local', showNotification = true) => {
        if (!supabase) return { success: false, status: null, data: null, message: 'Supabase client not available.' };
        const { error } = await supabase.auth.signOut({ scope });
        const result = error
          ? { success: false, status: null, data: null, message: error.message }
          : { success: true, status: 200, data: null, message: 'Signed out successfully.' };
        if (showNotification) notify(result);
        return result;
      },
    };
  }, [supabase, globalActions, useDevProxy]);

  return (
    <GlobalActionsProvider contextName="SupabaseActions" actions={actions}>
      {children}
    </GlobalActionsProvider>
  );
};